// DEPENDENCIES
import { useEffect, useRef, useState } from "react";

// CSS
import "./TanStackTableFilter.css";

// MUI IMPORTS
import {
	Box,
	Chip,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";

// CONSTANTS
import { EARLIEST_DATE, NA_FILTER_VALUE } from "../TanStackTableConstants";
import TanStackTableFilterPopover from "./TanStackTableFilterPopover/TanStackTableFilterPopover";

const TanStackTableFilter = (props) => {
	// STATES
	const [pendingColumn, setPendingColumn] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [filterMode, setFilterMode] = useState("contains");
	const [inputValue, setInputValue] = useState("");
	const [dateRange, setDateRange] = useState({ start: null, end: null });
	const [sortByNotApplicable, setSortByNotApplicable] = useState(false);
	const [selectOpen, setSelectOpen] = useState(false);
	const [popoverError, setPopoverError] = useState("");

	// CONSTS/REFS
	const filterRef = useRef(null);
	const selectedValues = props.columnFilters.map((f) => f.id);
	const open = Boolean(anchorEl);

	// EFFECTS
	useEffect(() => {
		if (props.setFilterResetter) {
			props.setFilterResetter(() => () => {
				props.setColumnFilters([]);
				handlePopoverClose();
			});
		}
	}, []);

	// HANDLERS
	// Remove one column filter from the active list
	const handleDelete = (columnToDelete) => {
		props.setColumnFilters((prev) =>
			prev.filter((f) => f.id !== columnToDelete)
		);
	};

	// Close the popover and reset all states
	const handlePopoverClose = () => {
		setAnchorEl(null);
		setPendingColumn(null);
		setInputValue("");
		setDateRange({ start: null, end: null });
		setFilterMode("contains");
		setSortByNotApplicable(false);
		setSelectOpen(false);
		setPopoverError("");
	};

	// This helper builds the new filter object for whichever filter type
	const handleAddColumnFilter = () => {
		if (!pendingColumn) return;
		const pendingColumnDef = props.columns.find(
			(col) => col.accessorKey === pendingColumn
		);
		if (!pendingColumnDef) {
			handlePopoverClose();
			return;
		}

		// remove any old filter for this same column before adding a new one
		const baseFilters = props.columnFilters.filter(
			(f) => f.id !== pendingColumn
		);

		//Checkbox
		if (
			pendingColumnDef.filterType === "checkbox" &&
			Array.isArray(inputValue)
		) {
			// if no boxes checked and N/A toggle is off, do nothing
			if (inputValue.length === 0 && !sortByNotApplicable) {
				setPopoverError(
					"Please select at least one option or toggle 'Not Applicable'."
				);
				return;
			}

			const newValue = sortByNotApplicable
				? NA_FILTER_VALUE
				: inputValue.map((v) => v.label);

			props.setColumnFilters([
				...baseFilters,
				{ id: pendingColumn, value: newValue },
			]);
		}
		// Date
		else if (pendingColumnDef.filterType === "date") {
			const { start, end } = dateRange;
			// if neither date chosen and not “does not exist”, do nothing
			if (!start && !end && !sortByNotApplicable) {
				setPopoverError(
					"Please select a date range or toggle 'Not Applicable'."
				);
				return;
			}
			// if both chosen but end < start, do nothing
			if (start && end && end.isBefore(start)) {
				setPopoverError("End date must be after start date.");
				return;
			}

			// adjust to defaults if one side missing
			const adjustedStart = start ? start : EARLIEST_DATE;
			const adjustedEnd = end ? end : new Date();

			const newValue = sortByNotApplicable
				? NA_FILTER_VALUE
				: {
						start: adjustedStart.toISOString(),
						end: adjustedEnd.toISOString(),
				  };

			props.setColumnFilters([
				...baseFilters,
				{ id: pendingColumn, value: newValue },
			]);
		}
		// text
		else {
			// if empty string and not dne, do nothing
			if (inputValue === "" && !sortByNotApplicable) {
				setPopoverError(
					"Please enter a value or toggle 'Not Applicable'."
				);
				return;
			}

			const newValue = sortByNotApplicable ? NA_FILTER_VALUE : inputValue;
			props.setColumnFilters([
				...baseFilters,
				{ id: pendingColumn, value: newValue },
			]);
		}

		handlePopoverClose();
	};

	// HELPERS
	// Helper to format a filter’s value into a string for display in the Chip
	const formatFilterValue = (filter, colDef) => {
		const raw = filter?.value;
		if (raw === NA_FILTER_VALUE) {
			return "Not Applicable";
		}

		if (colDef.filterType === "checkbox" && Array.isArray(raw)) {
			return raw.join(", ");
		}

		if (colDef.filterType === "date" && raw?.start && raw?.end) {
			const startStr = new Date(raw.start).toLocaleDateString("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
			const endStr = new Date(raw.end).toLocaleDateString("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
			return `${startStr} → ${endStr}`;
		}

		// default / text case
		return raw ?? "";
	};

	// RENDER
	return (
		<div id="tanStackTableFilterContainer">
			<Box ref={filterRef}>
				{/* Filter by Column Select */}
				<FormControl fullWidth>
					<InputLabel id="filterInputLabel">Filter...</InputLabel>
					<Select
						label="Filter..."
						labelId="filterInputLabel"
						multiple
						value={selectedValues}
						onOpen={(e) => {
							// If popover is open, prevent the Select dropdown from opening
							if (open) {
								e.preventDefault();
							} else {
								setSelectOpen(true);
							}
						}}
						onClose={() => {
							setSelectOpen(false);
						}}
						open={selectOpen && !open}
						onChange={(e) => {
							const newSelected = e.target.value; // array of accessorKeys
							const added = newSelected.filter(
								(k) => !selectedValues.includes(k)
							);
							if (added.length > 0) {
								const newKey = added[0];
								const colDef = props.columns.find(
									(c) => c.accessorKey === newKey
								);
								setPendingColumn(newKey);
								setInputValue(
									colDef.filterType === "checkbox" ? [] : ""
								);
								setDateRange({ start: null, end: null });
								setSortByNotApplicable(false);
								setAnchorEl(filterRef.current);
							}

							const removed = selectedValues.filter(
								(k) => !newSelected.includes(k)
							);
							if (removed.length > 0) {
								handleDelete(removed[0]);
							}
						}}
						renderValue={(selected) => (
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 0.5,
								}}
							>
								{selected.map((colKey) => {
									const colDef = props.columns.find(
										(c) => c.accessorKey === colKey
									);
									const filter = props.columnFilters.find(
										(f) => f.id === colKey
									);
									const valueStr = formatFilterValue(
										filter,
										colDef
									);

									return (
										<Chip
											key={colKey}
											label={`${colDef.label}: ${valueStr}`}
											onDelete={(e) => {
												e.stopPropagation();
												handleDelete(colKey);
											}}
											onMouseDown={(e) => {
												e.stopPropagation();
											}}
										/>
									);
								})}
							</Box>
						)}
						fullWidth
					>
						{props.columns
							.filter(
								(col) =>
									col.accessorKey &&
									col.enableSorting !== false
							)
							.map((col) => (
								<MenuItem
									key={col.accessorKey}
									value={col.accessorKey}
									disabled={props.columnFilters.some(
										(f) => f.id === col.accessorKey
									)}
								>
									{col.label}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				{/* Popover for adding new filter */}
				<TanStackTableFilterPopover
					open={open}
					anchorEl={anchorEl}
					columns={props.columns}
					pendingColumn={pendingColumn}
					filterMode={filterMode}
					setFilterMode={setFilterMode}
					setSortByNotApplicable={setSortByNotApplicable}
					setInputValue={setInputValue}
					setDateRange={setDateRange}
					sortByNotApplicable={sortByNotApplicable}
					inputValue={inputValue}
					handleAddColumnFilter={handleAddColumnFilter}
					handlePopoverClose={handlePopoverClose}
					dateRange={dateRange}
					popoverError={popoverError}
					setPopoverError={setPopoverError}
				/>
				{/* Popover end */}
			</Box>
		</div>
	);
};

export default TanStackTableFilter;
